/* eslint-disable no-console */
import * as _ from 'lodash';
import * as utils from './utils';
import * as tunnel from './commons/testimTunnel';
import * as perf from './commons/performance-logger';
import * as gridService from './services/gridService';
import * as concurrencyCompanyService from './services/concurrencyCompanyService';
import * as analytics from './commons/testimAnalytics';
import * as branchService from './services/branchService';
import * as servicesApi from './commons/testimServicesApi';
import * as testimCustomToken from './commons/testimCustomToken';
import * as runnerFileCache from './commons/runnerFileCache';
import { getLogger } from './commons/logger';
import { reporter } from './reports/reporter';
import { EDITOR_URL } from './commons/config';
import { CLI_MODE, gridTypes } from './commons/constants';
import { ArgError, QuotaDepletedError } from './errors';
import { TestPlanRunner } from './runners/TestPlanRunner';
import { socketService } from './commons/socket/socketService';
import { labFeaturesService } from './services/labFeaturesService';
import { featureFlagsService } from './commons/featureFlagsService';
import { featureAvailabilityService } from './commons/featureAvailabilityService';
import { prepareMockNetwork, initializeUserWithAuth } from './commons/prepareRunner';
import { getResolvedExtensionUrl } from './runOptionsUtils';
import type { RunnerOptions, TunnelOptions } from './runOptions';

const FREE_PLAN_MINIMUM_BROWSER_TIMEOUT = 30 * 60 * 1000;
const FREE_PLAN_ALLOWED_PARALLEL = 1;
const TEST_STAGE_NAME = 'Testim Automation';

const logger = getLogger('runner');

async function validateProjectQuotaNotDepleted(options: RunnerOptions) {
    const projectId = options.project;

    const usage = await servicesApi.getUsageForCurrentBillingPeriod(projectId);
    if (!usage?.isExecutionBlocked) {
        return;
    }

    console.error('You have reached the limit of runs for the billing month, please upgrade your plan at https://www.testim.io/upgrade-contact-us?source=cli');
    analytics.track(options.authData!.uid, 'execution-quota-surpassed', { projectId });
    throw new QuotaDepletedError();
}

const PARALLEL_QUOTA_TYPES = {
    desktopWeb: 'web',
    mobileWeb: 'web',
    android: 'mobile',
    ios: 'mobile',
    sfdc: 'sfdc',
} as const;

function validateOptionsForCompany(options: CommonSharedOptions, company: servicesApi.InitializedUserWithAuth['companyByProjectId']) {
    const planType = utils.getPlanType(company.activePlan);
    const optionsParallel = options.parallel!;

    if (planType === 'free' && optionsParallel > FREE_PLAN_ALLOWED_PARALLEL) {
        throw new ArgError(`The free plan allows only ${FREE_PLAN_ALLOWED_PARALLEL} parallel runs, please upgrade your plan at https://www.testim.io/upgrade-contact-us?source=cli. Run aborted`);
    }

    if (company.followL2CPlan) {
        const projectQuotaType = options.projectData?.type ? PARALLEL_QUOTA_TYPES[options.projectData.type] : undefined;
        const projectParallelMaxAmount = company.activePlan?.premiumFeatures.parallelism && projectQuotaType ? company.activePlan?.premiumFeatures.parallelism[projectQuotaType] : 0;

        if (projectParallelMaxAmount && optionsParallel > projectParallelMaxAmount) {
            console.warn(`It looks like your command used more parallel processing than your plan supports (${optionsParallel} vs. ${projectParallelMaxAmount}).\nWe'll use the maximum of ${projectParallelMaxAmount} for now. To use more, you can adjust your command or upgrade your plan.`);
            options.parallel = projectParallelMaxAmount;
        }
        const tunnelAllowed = company.activePlan?.premiumFeatures?.tunnel;
        if (options.tunnel && !tunnelAllowed) {
            throw new ArgError('Tunnel is not allowed for your current plan, please upgrade your plan at https://www.testim.io/upgrade-contact-us?source=cli. Run aborted');
        }

        const turboModeAllowed = company.activePlan?.premiumFeatures?.runInTurboMode;
        if (options.lightweightMode && !turboModeAllowed) {
            console.warn('\nTurbo mode is not allowed for your current plan, please upgrade your plan at https://www.testim.io/upgrade-contact-us?source=cli. Turbo mode disabled for this run.');
            options.lightweightMode = undefined;
        }
    }

    if (company.tvcLicense && options.gridData?.type === gridTypes.TESTIM_TVC) {
        const expireAt = company.tvcLicense.expireAt || 1;
        if (Date.now() > expireAt && !company.churnedByL2C) {
            throw new ArgError('Your license has expired. Please contact us at https://www.testim.io/upgrade-contact-us?source=cli to renew your license. Run aborted');
        }

        const maxParallel = Number(company.plan.premiumFeatures?.parallelism?.mobile);
        if (optionsParallel > maxParallel) {
            throw new ArgError(`Your license allows only ${maxParallel} parallel runs. Please contact us to upgrade your license at https://www.testim.io/upgrade-contact-us?source=cli. Run aborted`);
        }
    }

    if (company.tdcLicense && options.gridData?.type === gridTypes.TESTIM_TDC) {
        const expireAt = company.tdcLicense.expireAt || 1;
        if (Date.now() > expireAt) {
            throw new ArgError('Your license has expired. Please contact us at https://www.testim.io/upgrade-contact-us?source=cli to renew your license. Run aborted');
        }

        // TODO: add max parallel check for TDC license ?
    }

    const optionsRetention = options.retentionDays;
    if (optionsRetention) {
        const companyRetention = company.activePlan?.premiumFeatures?.resultRetention;
        if (optionsRetention > companyRetention) {
            throw new ArgError(`Retention days (${optionsRetention}) cannot be greater than the company's retention days (${companyRetention}). Run aborted`);
        }
    }

    //Check for abusing mobile projects.
    if (utils.isMobileProject(options as RunnerOptions)) {
        if (featureFlagsService.flags.removeMobileTrialRestrictions.isEnabled()) {
            return;
        }
        const tvcLicense = company.tvcLicense;
        const tdcLicense = company.tdcLicense;

        if (tvcLicense?.isPaid || tdcLicense?.isPaid) {
            return; // We trust paying customers
        }
        // The license's are free or trial

        const companyCreatedAt = company?.createdAt;
        if (new Date(companyCreatedAt!) < new Date(featureFlagsService.flags.applyMobileRestrictionFromDate.getValue())) {
            return;
        }

        // The company started the trial after the abuse date, reduce test timeout to 10 minutes if needed.
        const testsTimeout = options.timeout;
        if (testsTimeout >= 600_000) {
            options.timeout = 600_000;
            console.warn('Mobile Trial projects are limited to 10 minutes of execution time. The timeout has been reduced to 10 minutes.');
        }

        const testRetries = options.retries;
        if (testRetries && testRetries >= 1) {
            options.retries = 0;
            console.warn('Mobile Trial projects are not allowed to have test retries.');
        }
    }
}

async function validateCliAccount(options: RunnerOptions) {
    if (options.lightweightMode?.disableQuotaBlocking) {
        return;
    }
    try {
        await validateProjectQuotaNotDepleted(options);
    } catch (err) {
        if ([ArgError, QuotaDepletedError].some(errType => err instanceof errType)) {
            throw err;
        }
        logger.error('could not validate cli account', { err });
    }
}

function analyticsIdentify(projectId: string) {
    const authData = testimCustomToken.getTokenV3UserData();
    return analytics.identify({
        userId: authData.uid,
        name: authData.uid!,
        traits: {
            projectId,
            company: {
                id: projectId,
                // @ts-expect-error seems wrong?
                lastCIRun: Math.floor(Date.now() / 1000), // unix timestamp
            },
        },
    });
}

async function initSocketServices(projectId: string, { disableResults = false, disableRemoteStep = false }) {
    if (featureFlagsService.flags.useNewWSCLI.isEnabled() && !disableResults && !disableRemoteStep) {
        return socketService.connect(projectId);
    }
    if (!disableRemoteStep) {
        const { remoteStepService } = await import('./commons/socket/remoteStepService');
        await remoteStepService.init(projectId);
    }
    if (!disableResults) {
        const { testResultService } = await import('./commons/socket/testResultService');
        testResultService.init(projectId);
    }
    return undefined;
}

function setBranch(options: CommonSharedOptions, branchInfoFromServer: servicesApi.InitializedUserWithAuth['branchName']) {
    const { branch, autoDetect } = options;
    branchService.setCurrentBranch(branchInfoFromServer, autoDetect);
    if (!branchInfoFromServer && !autoDetect) {
        throw new ArgError(`branch ${branch} does not exist, run aborted.`);
    }
}

async function setSfdcCredential(options: CommonSharedOptions, project: servicesApi.InitializedUserWithAuth['projectById']) {
    const { id: projectId, type } = project;
    const sfdcCompany = _.get(options, 'company.activePlan.premiumFeatures.ttaForSalesforce');
    const shouldLoadSfdcCredential = sfdcCompany || type === 'sfdc' || (type === 'desktopWeb' && featureFlagsService.flags.sfdcHybridCompany.isEnabled());

    const branch = branchService.getCurrentBranch();
    if (shouldLoadSfdcCredential) {
        options.sfdcCredential = await servicesApi.loadSfdcCredential({ projectId, branch });
    }
}

interface TestDataFile {
    testData: string;
}
async function setExecutionTestData(options: CommonSharedOptions) {
    if (!options.testDataFileName) {
        return;
    }
    const { testDataFileName } = options;
    let s3Url: string | undefined;
    try {
        const response = await servicesApi.getFileUrl<{ s3Url: string }>(`fileName=${testDataFileName}&action=download&fileType=test-data&projectId=${options.project}`);
        s3Url = response.s3Url;
        const file = await fetch(s3Url);
        const fileContent = await file.json() as TestDataFile;
        analytics.trackWithCIUser('test-data-file-used-in-cli', { projectId: options.project, testDataFileId: testDataFileName, source: options.source });
        options.testDataFileName = testDataFileName;
        options.testDataString = fileContent.testData;
    } catch (err) {
        const urlInfo = s3Url ? `, s3Url: ${s3Url}` : '';
        throw new ArgError(`Failed to read/open test data file error: ${err.message}${urlInfo}`);
    }}

function setCompany(options: CommonSharedOptions, company: servicesApi.InitializedUserWithAuth['companyByProjectId']) {
    const { id, name, activePlan = {} } = company;
    const isPOC = Boolean(activePlan.isPoc);
    const isStartUp = Boolean(activePlan.isStartUp);
    const planType = utils.getPlanType(activePlan);
    if (planType === 'free') {
        options.newBrowserWaitTimeout = options.newBrowserWaitTimeout! < FREE_PLAN_MINIMUM_BROWSER_TIMEOUT ? FREE_PLAN_MINIMUM_BROWSER_TIMEOUT : options.newBrowserWaitTimeout;
    }
    featureFlagsService.setCompanyId(id);
    featureFlagsService.setIsPOC(isPOC);
    featureFlagsService.setIsStartUp(isStartUp);
    featureFlagsService.setPlanType(planType);
    featureAvailabilityService.setPlanType(planType);
    options.company = {
        ucid: '', // (Dror)
        companyId: id,
        name,
        planType,
        isPOC,
        isStartUp,
        activePlan,
    };
}

function setSystemInfo(options: CommonSharedOptions, editorUrl: servicesApi.InitializedUserWithAuth['clientConfig']['editorUrl']) {
    options.editorUrl = EDITOR_URL || editorUrl;
}

function setAllGrids(options: CommonSharedOptions, allGrids: servicesApi.InitializedUserWithAuth['allGrids']) {
    options.allGrids = allGrids;
}

function setAuthData(options: CommonSharedOptions, authData: servicesApi.InitializedUserWithAuth['authData']) {
    options.authData = authData;
}

function setProject(options: CommonSharedOptions, project: servicesApi.InitializedUserWithAuth['projectById']) {
    const { id, name, type, defaults } = project;
    featureFlagsService.setProjectId(id);
    featureFlagsService.setProjectType(type!);
    options.projectData = {
        projectId: id,
        type,
        name,
        defaults,
    };
}

async function setGrid(options: CommonSharedOptions) {
    options.gridData = await gridService.getGridData(options);
    if (options.gridData?.name) {
        options.grid = options.gridData.name;
    }
}
function setMobileMode(options: CommonSharedOptions, project: servicesApi.InitializedUserWithAuth['projectById']) {
    options.mode = ['android', 'ios'].includes(project.type || '') ? 'appium' : options.mode;
}

async function setMockNetworkRules(options: CommonSharedOptions) {
    const { project } = options;
    const props = { projectId: project };

    if (options.overrideMappingFile) {
        analytics.trackWithCIUser('user-override-file', props);
        options.mockNetworkRules = await prepareMockNetwork(options.overrideMappingFile);
    }
}

async function setCustomCapabilities(options: CommonSharedOptions) {
    if (options.customCapabilitiesName) {
        const data = await servicesApi.getCustomCapabilitiesByName(options.project, options.customCapabilitiesName);

        if (!data) {
            throw new ArgError(`"${options.customCapabilitiesName}" custom capabilities not found`);
        }

        options.customCapabilities = data.capabilities;
    }
}

async function runRunner(options: RunnerOptions, customExtensionLocalLocation?: string) {
    perf.log('in runner.js runRunner');

    const { project, remoteRunId, useLocalChromeDriver, useChromeLauncher } = options;

    if (!remoteRunId) {
        options.source = (useLocalChromeDriver || useChromeLauncher) ? 'cli-local' : 'cli';
    }

    await validateCliAccount(options);

    perf.log('in runRunner before tunnel.connect');
    await tunnel.connect(options);
    perf.log('in runRunner after tunnel.connect');

    const testPlanRunner = new TestPlanRunner(customExtensionLocalLocation);
    const results = await testPlanRunner.run(options);

    perf.log('before tunnel.disconnect');
    await tunnel.disconnect(options);
    await endKeepAlive(options.slotService, project, options.company!.companyId);
    perf.log('after tunnel.disconnect and gridService.keepAlive.end');

    return results;
}
export const run = runRunner;

function showFreeGridRunWarningIfNeeded(options: CommonSharedOptions) {
    if (featureAvailabilityService.shouldShowFreeGridRunWarning(options.gridData?.type)) {
        const CYAN = '\x1b[36m';
        const UNDERSCORE = '\x1b[4m';
        const RESET = '\x1b[0m';
        const MESSAGE = 'Our Free grid offers basic service performance.\nIf you need faster results, contact us to upgrade your plan and dramatically improve your tests’ run times.';
        console.log(`\n${UNDERSCORE}${CYAN}${MESSAGE}${RESET}\n`);
    }
}

function startKeepAlive(slotService: 'mongo' | 'redis' | undefined, project: string, companyId: string) {
    if (slotService === 'redis') {
        concurrencyCompanyService.keepAlive.start(companyId, project);
    }
    if (slotService === 'mongo') {
        gridService.keepAlive.start(project);
    }
}

async function endKeepAlive(slotService: 'mongo' | 'redis', project: string, companyId: string) {
    if (slotService === 'redis') {
        await concurrencyCompanyService.keepAlive.end(companyId, project);
    }
    if (slotService === 'mongo') {
        await gridService.keepAlive.end(project);
    }
}

/* eslint "@typescript-eslint/sort-type-constituents": error */
type PartialKeysFromRunOptions = (
    | 'allGrids'
    | 'authData'
    | 'autoDetect'
    | 'branch'
    | 'browser'
    | 'company'
    | 'customCapabilities'
    | 'customCapabilitiesName'
    | 'disableSockets'
    | 'editorUrl'
    | 'ext'
    | 'extensionLocation'
    | 'extensionPath'
    | 'grid'
    | 'gridData'
    | 'gridId'
    | 'host'
    | 'lightweightMode'
    | 'localTmaUrl'
    | 'mockNetworkRules'
    | 'mode'
    | 'newBrowserWaitTimeout'
    | 'overrideMappingFile'
    | 'parallel'
    | 'path'
    | 'perfecto'
    | 'playerPath'
    | 'port'
    | 'printGrids'
    | 'projectData'
    | 'protocol'
    | 'publicIps'
    | 'retentionDays'
    | 'saucelabs'
    | 'sfdcCredential'
    | 'slotService'
    | 'testobjectSauce'
    | 'testOptimization'
    | 'testPlan'
    | 'testPlanIds'
    | 'useChromeLauncher'
    | 'useLocalChromeDriver'
);
/* eslint-disable @typescript-eslint/sort-type-constituents */

export type CommonSharedOptions = (
    & Pick<RunnerOptions, 'project' | 'token' | 'userAccessKey' | 'timeout' | 'retries' | 'userParamsData' | 'testDataFileName' | 'testDataString' | 'source'> // <---- required
    & Partial<Pick<TunnelOptions, 'tunnelOnlyMode' | 'tunnel'>> // <---- optional tunnel
    & Partial<Pick<RunnerOptions, PartialKeysFromRunOptions>> // <---- optional
);

/**
 * This method initializes the Testim CLI with all the information it needs to start executing, it takes care of:
 *
 * - Reporting the user to analytics
 * - Authenticating the user and exchanging their token for a jwt
 * - Sets the grids for the company and validates the user has permission to run the CLI
 * @param options - the run options passed to the CLI, namely the project and token
 */
export async function init(options: CommonSharedOptions) {
    perf.log('start runner init');
    const { project, lightweightMode, useChromeLauncher, mode, disableSockets } = options;
    const socketConnected = initSocketServices(project, {
        disableResults: disableSockets || Boolean(lightweightMode?.disableResults && (useChromeLauncher || mode === CLI_MODE.SELENIUM)),
        disableRemoteStep: disableSockets || Boolean(lightweightMode?.disableRemoteStep),
    });

    socketConnected.catch(() => { /* ignore */ });

    const { authData, companyByProjectId, projectById, branchName, allGrids, clientConfig } = await initializeUserWithAuth(options);

    if (companyByProjectId.disabled) {
        throw new ArgError('The company is disabled.');
    }

    if (projectById.disabled) {
        throw new ArgError('The project is disabled.');
    }

    if (options.printGrids) {
        // print all the available grids for the company
        const allGridsSorted = allGrids.sort((a, b) => a.type.localeCompare(b.type));
        console.table(allGridsSorted.reduce<any>((tableData, grid) => {
            class Grid { Type: string; Browsers: any; constructor(type: string, browsers: any) { this.Type = type; this.Browsers = browsers; } } // classes print better in tables
            const gridBrowsersAsString = Object.entries(grid.browsers || { 'N/A': '' }).map(i => i.join(':')).join(', ');
            tableData[grid.name] = new Grid(grid.type, gridBrowsersAsString);
            return tableData;
        }, {}));
        process.exit(0); // doesn't make sense to go on with run if the user wanted to get grid info (in other words, choose a grid)
    }

    await socketConnected;

    perf.log('after featureFlagsReady and socketConnected');
    setSystemInfo(options, clientConfig.editorUrl);
    setCompany(options, companyByProjectId);
    setProject(options, projectById);
    setMobileMode(options, projectById);
    setBranch(options, branchName);
    setAllGrids(options, allGrids);
    setAuthData(options, authData);
    await setExecutionTestData(options);
    await setSfdcCredential(options, projectById);
    await setGrid(options);
    await setCustomCapabilities(options);

    validateOptionsForCompany(options, companyByProjectId);

    // prepare extension after setting authData
    if (!options.extensionLocation?.length) {
        options.extensionLocation = [getResolvedExtensionUrl(options)];
    }

    /** Remotely purge client cache - consider using with the rollout FF "scheduled" option to create a window where cache is cleared that goes back to normal afterwards  */
    if (featureFlagsService.flags.clearRunnerFileCache.isEnabled()) {
        try {
            await runnerFileCache.clear();
            logger.info('runner file cache cleared');
        } catch (err) {
            logger.error('failed to clear runner file cache', { err });
        }
    }

    if (!options.lightweightMode?.disableLabs) {
        await labFeaturesService.loadLabFeatures(projectById.id, companyByProjectId.activePlan);
    }

    if (options.lightweightMode?.type === 'turboMode' && (featureFlagsService.flags.highSpeedMode.getValue() === 'disabled' || options.company!.planType === 'free')) {
        delete options.lightweightMode;
    }

    if (options.lightweightMode?.type === 'turboMode') {
        // eslint-disable-next-line max-len
        console.log('\nTurbo mode will ignore step delays. Test artifacts like screenshots and logs will only be saved for failed runs. For more information see our docs: https://docs.tricentis.com/testim/content/testops/turbo-mode.htm');
    }

    startKeepAlive(options.slotService, project, companyByProjectId.id);
    analyticsIdentify(project);
    await setMockNetworkRules(options);
    showFreeGridRunWarningIfNeeded(options);

    const branchToUse = branchService.getCurrentBranch();
    await reporter.setOptions(options as RunnerOptions, branchToUse);

    //track sealights stage name analytics
    if (options.testOptimization?.seaLights?.testStage && options.testOptimization.seaLights.testStage !== TEST_STAGE_NAME) {
        analytics.track(authData.uid, 'cli-sealights_stage_changed', { testStage: options.testOptimization.seaLights.testStage, projectId: project });
    }
}
